import { inject, observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import BroadcastStreamStore from "../_stores/broadcast-stream/BroadcastStreamStore";
import PricingStore from "../_stores/pricing/PricingStore";
import ProductDropdown from "../broadcast-pricing-product-dropdown";
import ValidationStore from "library/core/stores/validation/ValidationStore";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindIcon from "library/components/_tailwind/icon";
import TailwindButton from "library/components/_tailwind/button";
import TailwindTranslatedText from "library/components/_tailwind/translated-text";
import TailwindDropdown, {
  TailwindDropdownValue,
} from "library/components/_tailwind/dropdown";
import {
  BROADCASTER_IS_CONNEXION_ONE_WAY_ENABLED,
  BROADCASTER_IS_CONNEXION_TWO_WAY_ENABLED,
  BROADCASTER_IS_PARTY_CHAT_ENABLED,
} from "../_stores/broadcast/consts";
import StudioModelAccessRights from "../../studio-models-access-rights";
import ProfileStore from "common/my-page/stores/profile/ProfileStore";
import HelpTooltip from "library/components/_tailwind/help-tooltip";
import TailwindBox from "library/components/_tailwind/box";
import LayoutStore from "library/core/stores/layout/LayoutStore";
import BroadcastingSettingsModal from "../broadcasting-settings-modal";
import { MODAL_WRAPPER_CLASSNAME } from "library/core/stores/modal/consts";
import NodeChatStore from "../_stores/nodechat/NodeChatStore";
import LanguageStore from "library/core/stores/language/LanguageStore";
import { LANGUAGES } from "library/i18n/locales";
import { VoiceOption } from "common/live-translation/LiveTranslationService";
import { snackbarStore } from "library/core/stores/snackbar/SnackbarStore";
import { SnackbarVariants } from "library/core/stores/snackbar/enums";
import AiChatStore from "../_stores/ai-chat/AiChatStore";
import AuthStore from "core/stores/auth/AuthStore";
import { FEATURE_FLAGS } from "core/stores/auth/feature-flags";
import { AiChatPosition } from "../_stores/ai-chat/types";

interface Props {
  broadcastStore?: BroadcastStore;
  broadcastStreamStore?: BroadcastStreamStore;
  pricingStore?: PricingStore;
  validationStore?: ValidationStore;
  profileStore?: ProfileStore;
  layoutStore?: LayoutStore;
  nodeChatStore?: NodeChatStore;
  languageStore?: LanguageStore;
  aiChatStore?: AiChatStore;
  authStore?: AuthStore;
}

type SettingsOptionRowProps = {
  label: React.ReactNode;
  control: React.ReactNode;
  narrowWidth?: "md:w-40" | "md:w-52";
};

const SettingsOptionRow: React.FC<SettingsOptionRowProps> = ({
  label,
  control,
  narrowWidth = "md:w-40",
}) => (
  <TailwindFlex
    alignItems='items-center'
    justifyContent='justify-between'
    margin={["mb-5"]}
    gap='gap-3'>
    <TailwindBox
      flexGrow='flex-grow'
      flexShrink='flex-shrink'
      minWidth='min-w-0'>
      {label}
    </TailwindBox>
    <TailwindBox
      width='w-60'
      flexShrink='flex-shrink-0'
      pseudoClasses={[narrowWidth]}>
      {control}
    </TailwindBox>
  </TailwindFlex>
);

const BroadcastSettingsPopover: React.FunctionComponent<
  Props & WrappedComponentProps
> = ({
  broadcastStore,
  broadcastStreamStore,
  pricingStore,
  validationStore,
  profileStore,
  nodeChatStore,
  languageStore,
  aiChatStore,
  authStore,
}) => {
  const {
    hideVideoSettingsPopover,
    isShowStarted,
    liveTranslationService,
    isLiveTranslationActive,
  } = broadcastStore!;
  const { faceBlurEnabled, setFaceBlurEnabled, allowFaceBlur } =
    broadcastStreamStore!;
  const { setFanWhisperFree, setMembersNeedBalanceToChat } = nodeChatStore!;
  const { intl, language, updateCookieAndSetLang } = languageStore!;
  const {
    modelProfile: { access_rights, gender },
    isStudioModel,
  } = profileStore!;
  const {
    formatPricesByID,
    modelProducts,
    submitNewPrice,
    submitPrice,
    submitIsRecordingPrivate,
    connexionOneWayPriceOptions,
    connexionTwoWayPriceOptions,
    partyChatPriceOptions,
    whisperMessagePriceOptions,
    buzzPriceOptions,
    superBuzzPriceOptions,
    submitBuzzPrice,
    submitSystemSettings,
  } = pricingStore!;
  const { clearErrors } = validationStore!;

  const [voiceOptions, setVoiceOptions] = useState<VoiceOption[]>([]);
  const [selectedVoiceIdx, setSelectedVoiceIdx] = useState<number>(
    liveTranslationService?.getSelectedVoiceIdx() ?? 0
  );
  const [isPreviewPlaying, setIsPreviewPlaying] = useState<boolean>(false);

  const fetchVoices = async () => {
    // Initialize service if it doesn't exist
    if (!liveTranslationService) {
      broadcastStore!.initializeLiveTranslationService();
    }

    const service = broadcastStore!.liveTranslationService;
    if (!service) return;

    // Only fetch if we don't have voice options yet
    if (!service.voiceOptions) {
      await service.fetchVoiceOptions();
    }

    const options = service.getVoiceOptionsForGender(gender);
    setVoiceOptions(options);
    setSelectedVoiceIdx(service.getSelectedVoiceIdx());
  };

  useEffect(() => {
    clearErrors();

    // Fetch voice options on mount
    fetchVoices();

    // Cleanup: stop any playing preview when component unmounts
    return () => {
      const service = broadcastStore?.liveTranslationService;
      if (service) {
        service.stopVoicePreview();
      }
    };
  }, [gender, liveTranslationService, broadcastStore]);

  const clickAway = () => {
    hideVideoSettingsPopover() as any;
  };

  const onChangeIsRecordingAllPrivateShows = useCallback(isChecked => {
    submitIsRecordingPrivate(isChecked);
  }, []);

  const onChangeConnexionTwoWayPrice = useCallback(
    (value: any) => submitPrice("connexion_two_way", value),
    []
  );

  const onChangeConnexionOneWayPrice = useCallback(
    (value: any) => submitPrice("connexion_one_way", value),
    []
  );

  const onChangePartyChatPrice = useCallback(
    (value: any) => submitNewPrice("party_chat", "is_party_allowed", value),
    []
  );

  const onChangeWhisperMessagePrice = useCallback(
    (value: any) => submitPrice("whisper_message", value),
    []
  );

  const onChangeBuzzPrice = useCallback((value: TailwindDropdownValue) => {
    if (typeof value === "number") {
      submitBuzzPrice(true, value);
    } else {
      submitBuzzPrice(false);
    }
  }, []);

  const onChangeSoundEnterLeave = useCallback((value: any) => {
    submitSystemSettings({
      sound_enter_leave: value,
    });
  }, []);

  const onChangeFanClubWhisperFree = useCallback((value: any) => {
    setFanWhisperFree(value);
    submitSystemSettings({
      fan_whisper_free: value,
    });
  }, []);

  const onChangeAllowMembersWithFundToChat = useCallback((value: any) => {
    setMembersNeedBalanceToChat(value);
    submitSystemSettings({
      only_allow_members_with_fund_to_chat: value,
    });
  }, []);

  const onChangeWhisperTabsLayout = useCallback((value: any) => {
    submitSystemSettings({
      whispers_in_separate_tabs: value,
    });
  }, []);

  const isAiChatFeatureEnabled = authStore?.isFeatureEnabled(
    FEATURE_FLAGS.FEATURE_AI_CHAT_MESSAGES
  );

  const onChangeAiChatEnabled = useCallback(
    (value: TailwindDropdownValue) => {
      aiChatStore!.setIsUserEnabled(value as boolean);
    },
    []
  );

  const onChangeAiChatPosition = useCallback(
    (value: TailwindDropdownValue) => {
      aiChatStore!.setRecommendationPosition(value as AiChatPosition);
    },
    []
  );

  const onChangeFaceBlur = useCallback(
    async (value: any) => {
      try {
        await setFaceBlurEnabled(value);
        snackbarStore.enqueueSnackbar({
          message: {
            id: value
              ? "broadcast.face-blur-enabled"
              : "broadcast.face-blur-disabled",
            default: value
              ? "Face blur enabled - other faces will be blurred"
              : "Face blur disabled",
          },
          variant: SnackbarVariants.SUCCESS,
        });
      } catch (error) {
        snackbarStore.enqueueSnackbar({
          message: {
            id: "broadcast.face-blur-error",
            default: "Failed to change face blur setting. Please try again.",
          },
          variant: SnackbarVariants.ERROR,
        });
      }
    },
    [setFaceBlurEnabled]
  );

  const onChangeVoiceOption = useCallback(
    (value: any) => {
      const service = broadcastStore?.liveTranslationService;
      if (!service) return;

      // Stop any currently playing preview when switching voices
      if (service.isPreviewPlaying()) {
        service.stopVoicePreview();
        setIsPreviewPlaying(false);
      }

      const idx = typeof value === "number" ? value : parseInt(value, 10);
      setSelectedVoiceIdx(idx);
      service.setSelectedVoiceIdx(idx);

      // Show success toast message
      snackbarStore.enqueueSimpleSnackbar(
        "messages.success.chatSettingsUpdated",
        SnackbarVariants.SUCCESS
      );
    },
    [broadcastStore]
  );

  const onPlayVoicePreview = useCallback(() => {
    const service = broadcastStore?.liveTranslationService;
    if (!service) return;

    // If already playing, stop it
    if (service.isPreviewPlaying()) {
      service.stopVoicePreview();
      setIsPreviewPlaying(false);
      return;
    }

    // Ensure service has the latest selected voice index
    service.setSelectedVoiceIdx(selectedVoiceIdx);

    // Otherwise, start playing
    const selectedVoice = voiceOptions.find(v => v.idx === selectedVoiceIdx);
    if (selectedVoice) {
      service.playVoicePreview(selectedVoice);
      setIsPreviewPlaying(true);

      // Poll to check when playback ends
      const checkPlayback = setInterval(() => {
        if (!service.isPreviewPlaying()) {
          setIsPreviewPlaying(false);
          clearInterval(checkPlayback);
        }
      }, 100);
    }
  }, [voiceOptions, selectedVoiceIdx, broadcastStore]);

  return (
    <BroadcastingSettingsModal onClose={hideVideoSettingsPopover}>
      <TailwindBox className={[MODAL_WRAPPER_CLASSNAME]}>
        <TailwindFlex
          justifyContent={"justify-between"}
          position='relative'
          alignItems={"items-center"}>
          <TailwindTranslatedText
            descriptor={{
              id: "settings.other-options",
              defaultMessage: "Other options",
            }}
            paragraphProps={{
              fontSize: "text-xl",
              fontWeight: "font-bold",
              margin: ["mb-3"],
            }}
          />
          <TailwindButton
            position='absolute'
            padding={["p-0"]}
            inset={["-top-3", "-right-2"]}
            fullWidth={false}
            onClick={clickAway}>
            <TailwindIcon fontSize={"text-3xl"} name={"close"} />
          </TailwindButton>
        </TailwindFlex>
        <StudioModelAccessRights
          isIndividualModel={!isStudioModel}
          shouldShow={!!access_rights?.is_pricing_settings_change_allowed}
          type={"pricing"}
          boxProps={{
            margin: ["mb-2"],
          }}>
          <SettingsOptionRow
            label={
              <TailwindBox width='w-full' minWidth='min-w-0'>
                <TailwindTranslatedText
                  display='block'
                  textColor='text-main-color'
                  lineHeight='leading-tight'
                  descriptor={{
                    id: "free-to-enter-private.whisper",
                    defaultMessage: "Whisper/private message",
                  }}
                />
                <TailwindTranslatedText
                  display='block'
                  width='w-full'
                  margin={["mt-1"]}
                  textColor='text-main-color'
                  fontSize='text-xs'
                  lineHeight='leading-snug'
                  descriptor={{
                    id: "free-to-enter-private.whisper-subtext",
                    defaultMessage:
                      "Diamond Fan Club always free, Silver/Gold get 15/20 free whispers",
                  }}
                />
              </TailwindBox>
            }
            control={
              <ProductDropdown
                showSearch={false}
                className={["h-10"]}
                fullWidth={true}
                fullHeight={true}
                disabled={isShowStarted}
                options={whisperMessagePriceOptions}
                onChange={onChangeWhisperMessagePrice}
                price={modelProducts?.whisper_message}
                currency={intl.formatMessage({
                  id: `common.units.tokens-long`,
                  defaultMessage: "tokens",
                })}
                unit={intl.formatMessage({
                  id: `common.units.message-long`,
                  defaultMessage: "message",
                })}
              />
            }
          />
          <SettingsOptionRow
            label={
              <TailwindBox width='w-full' minWidth='min-w-0'>
                <TailwindTranslatedText
                  className='mr-1'
                  textColor='text-main-color'
                  descriptor={{
                    id: "broadcast-system-settings-control.fan-club",
                    defaultMessage: "Classic Fan Club members whisper for free",
                  }}
                />
                <HelpTooltip
                  tooltip={intl.formatMessage({
                    id: "broadcast-system-settings-control.fanclub-tooltip",
                    defaultMessage:
                      "Use this setting to encourage members to join your Fan Club, as well as limit the number of Whispers you receive during chat.",
                  })}
                />
              </TailwindBox>
            }
            control={
              <TailwindDropdown
                showSearch={false}
                className={["h-10"]}
                name='fan_whisper_free'
                fullHeight={true}
                fullWidth={true}
                borderColor={"border-gray-500"}
                backgroundColor={"primary"}
                borderWidth={["border"]}
                value={
                  modelProducts?.chat_settings?.system_settings?.fan_whisper_free
                }
                items={[
                  {
                    label: intl.formatMessage({
                      id: "common.yes",
                      defaultMessage: "Yes",
                    }),
                    value: true,
                  },
                  {
                    label: intl.formatMessage({
                      id: "common.no",
                      defaultMessage: "No",
                    }),
                    value: false,
                  },
                ]}
                onChange={onChangeFanClubWhisperFree}
              />
            }
          />
          <SettingsOptionRow
            label={
              <TailwindTranslatedText
                textColor='text-main-color'
                descriptor={{
                  id: "common.buzz",
                  defaultMessage: "Buzz",
                }}
              />
            }
            control={
              <ProductDropdown
                showSearch={false}
                className={["h-10"]}
                fullWidth={true}
                fullHeight={true}
                disabled={isShowStarted}
                options={buzzPriceOptions}
                onChange={onChangeBuzzPrice}
                price={formatPricesByID("buzz_price", "buzz_status")}
                currency={intl.formatMessage({
                  id: `common.units.tokens-long`,
                  defaultMessage: "tokens",
                })}
                unit={intl.formatMessage({
                  id: `common.units.buzz`,
                  defaultMessage: "buzz",
                })}
              />
            }
          />
          <SettingsOptionRow
            label={
              <TailwindTranslatedText
                textColor='text-main-color'
                descriptor={{
                  id: "payout-product-types.super_buzz",
                  defaultMessage: "SuperBuzz",
                }}
              />
            }
            control={
              <ProductDropdown
                showSearch={false}
                className={["h-10"]}
                fullWidth={true}
                fullHeight={true}
                disabled={true}
                options={superBuzzPriceOptions}
                onChange={() => {}}
                price={formatPricesByID("superbuzz_price", "buzz_status")}
                currency={intl.formatMessage({
                  id: `common.units.tokens-long`,
                  defaultMessage: "tokens",
                })}
                unit={intl.formatMessage({
                  id: `common.units.superbuzz`,
                  defaultMessage: "superbuzz",
                })}
              />
            }
          />
        </StudioModelAccessRights>
        {BROADCASTER_IS_PARTY_CHAT_ENABLED && (
          <SettingsOptionRow
            label={
              <TailwindTranslatedText
                textColor='text-main-color'
                descriptor={{
                  id: "free-to-enter-private.party",
                  defaultMessage: "Party show",
                }}
              />
            }
            control={
              <ProductDropdown
                showSearch={false}
                className={["h-10"]}
                fullWidth={true}
                fullHeight={true}
                disabled={isShowStarted}
                options={partyChatPriceOptions}
                onChange={onChangePartyChatPrice}
                price={formatPricesByID("party_chat", "is_party_allowed")}
                currency={intl.formatMessage({
                  id: `common.units.tokens-long`,
                  defaultMessage: "tokens",
                })}
                unit={intl.formatMessage({
                  id: `common.units.minute`,
                  defaultMessage: "minute",
                })}
              />
            }
          />
        )}

        {BROADCASTER_IS_CONNEXION_ONE_WAY_ENABLED && (
          <SettingsOptionRow
            label={
              <TailwindTranslatedText
                textColor='text-main-color'
                descriptor={{
                  id: "connexion.connexion1",
                  defaultMessage: "Connexion-1-way",
                }}
              />
            }
            control={
              <ProductDropdown
                showSearch={false}
                className={["h-10"]}
                fullWidth={true}
                fullHeight={true}
                disabled={isShowStarted}
                options={connexionOneWayPriceOptions}
                onChange={onChangeConnexionOneWayPrice}
                price={modelProducts?.connexion_one_way}
                currency={intl.formatMessage({
                  id: `common.units.tokens`,
                  defaultMessage: "tkns",
                })}
                unit={intl.formatMessage({
                  id: `common.units.min`,
                  defaultMessage: "min",
                })}
              />
            }
          />
        )}
        {BROADCASTER_IS_CONNEXION_TWO_WAY_ENABLED && (
          <SettingsOptionRow
            label={
              <TailwindTranslatedText
                textColor='text-main-color'
                descriptor={{
                  id: "connexion.connexion2",
                  defaultMessage: "Connexion-2-way",
                }}
              />
            }
            control={
              <ProductDropdown
                showSearch={false}
                className={["h-10"]}
                fullWidth={true}
                fullHeight={true}
                disabled={isShowStarted}
                options={connexionTwoWayPriceOptions}
                onChange={onChangeConnexionTwoWayPrice}
                price={modelProducts?.connexion_two_way}
                currency={intl.formatMessage({
                  id: `common.units.tokens`,
                  defaultMessage: "tkns",
                })}
                unit={intl.formatMessage({
                  id: `common.units.min`,
                  defaultMessage: "min",
                })}
              />
            }
          />
        )}

        <SettingsOptionRow
          label={
            <TailwindBox width='w-full' minWidth='min-w-0'>
              <TailwindTranslatedText
                className='mr-1'
                textColor='text-main-color'
                descriptor={{
                  id: "settings.record-private-party-shows",
                  defaultMessage:
                    "Automatically record my Private and Party shows",
                }}
              />
              <HelpTooltip
                className='dis'
                tooltip={intl.formatMessage({
                  id: "broadcast-system-settings-control.automatically-recorded",
                  defaultMessage:
                    "If this setting is turned on, your Private and Party shows lasting longer than 2 minutes will be recorded automatically. If this setting is turned off, you can manually record all your paid shows without any time limitations, including Nude and Hidden Goal shows.",
                })}
              />
            </TailwindBox>
          }
          control={
            <TailwindDropdown
              showSearch={false}
              className={["h-10"]}
              name='record_prviate_shows'
              fullHeight={true}
              fullWidth={true}
              borderColor={"border-gray-500"}
              backgroundColor={"primary"}
              borderWidth={["border"]}
              value={
                modelProducts?.chat_settings?.is_recording_all_private_shows
              }
              items={[
                {
                  label: intl.formatMessage({
                    id: "common.yes",
                    defaultMessage: "Yes",
                  }),
                  value: true,
                },
                {
                  label: intl.formatMessage({
                    id: "common.no",
                    defaultMessage: "No",
                  }),
                  value: false,
                },
              ]}
              onChange={onChangeIsRecordingAllPrivateShows}
            />
          }
        />

        <SettingsOptionRow
          label={
            <TailwindBox width='w-full' minWidth='min-w-0'>
              <TailwindTranslatedText
                className='mr-1'
                textColor='text-main-color'
                descriptor={{
                  id: "broadcast-system-settings-control.only-allow-members-with-fund-to-chat",
                  defaultMessage: "Who can chat with me",
                }}
              />
              <HelpTooltip
                className='dis'
                tooltip={intl.formatMessage({
                  id: "broadcast-system-settings-control.prevent-zero-fund",
                  defaultMessage:
                    "Prevent zero fund members from chatting with me.",
                })}
              />
            </TailwindBox>
          }
          control={
            <TailwindDropdown
              showSearch={false}
              className={["h-10"]}
              name='sound_enter_leave'
              fullHeight={true}
              fullWidth={true}
              borderColor={"border-gray-500"}
              backgroundColor={"primary"}
              borderWidth={["border"]}
              value={
                modelProducts?.chat_settings?.system_settings
                  ?.only_allow_members_with_fund_to_chat
              }
              items={[
                {
                  label: intl.formatMessage({
                    id: "broadcast-system-settings-control.everyone",
                    defaultMessage: "Everyone",
                  }),
                  value: false,
                },
                {
                  label: intl.formatMessage({
                    id: "broadcast-system-settings-control.paid-members-only",
                    defaultMessage: "Members with tokens",
                  }),
                  value: true,
                },
              ]}
              onChange={onChangeAllowMembersWithFundToChat}
            />
          }
        />

        <SettingsOptionRow
          label={
            <TailwindBox width='w-full' minWidth='min-w-0'>
              <TailwindTranslatedText
                className='mr-1'
                textColor='text-main-color'
                descriptor={{
                  id: "broadcast-system-settings-control.sound-alert",
                  defaultMessage:
                    "Play sound alert when viewers enter or leave",
                }}
              />
              <HelpTooltip
                tooltip={intl.formatMessage({
                  id: "broadcast-system-settings-control.enable-sound-enter-leave",
                  defaultMessage:
                    "Enable this setting to hear a sound alert when a member leaves the room and when a new member enters. This will help you stay on top of greeting new viewers as they enter the room.",
                })}
              />
            </TailwindBox>
          }
          control={
            <TailwindDropdown
              showSearch={false}
              className={["h-10"]}
              name='sound_enter_leave'
              fullHeight={true}
              fullWidth={true}
              borderColor={"border-gray-500"}
              backgroundColor={"primary"}
              borderWidth={["border"]}
              value={
                modelProducts?.chat_settings?.system_settings?.sound_enter_leave
              }
              items={[
                {
                  label: intl.formatMessage({
                    id: "common.yes",
                    defaultMessage: "Yes",
                  }),
                  value: true,
                },
                {
                  label: intl.formatMessage({
                    id: "common.no",
                    defaultMessage: "No",
                  }),
                  value: false,
                },
              ]}
              onChange={onChangeSoundEnterLeave}
            />
          }
        />
        <SettingsOptionRow
          label={
            <TailwindTranslatedText
              textColor='text-main-color'
              descriptor={{
                id: "broadcast-system-settings-control.set-language",
                defaultMessage: "Set default language",
              }}
            />
          }
          control={
            <TailwindDropdown
              showSearch={false}
              className={["h-10"]}
              name='lang-picker'
              fullHeight={true}
              fullWidth={true}
              borderColor={"border-gray-500"}
              backgroundColor={"primary"}
              borderWidth={["border"]}
              value={language}
              items={LANGUAGES.map(lang => {
                return {
                  label: lang.name,
                  value: lang.key,
                };
              })}
              onChange={e => {
                updateCookieAndSetLang(e as string);
              }}
            />
          }
        />

        {isAiChatFeatureEnabled && (
          <>
            <SettingsOptionRow
              label={
                <TailwindBox width='w-full' minWidth='min-w-0'>
                  <TailwindTranslatedText
                    className='mr-1'
                    textColor='text-main-color'
                    descriptor={{
                      id: "broadcast-system-settings-control.ai-chat-enabled",
                      defaultMessage: "AI chat suggestions",
                    }}
                  />
                  <HelpTooltip
                    tooltip={intl.formatMessage({
                      id: "broadcast-system-settings-control.ai-chat-enabled-tooltip",
                      defaultMessage:
                        "Enable or disable AI-generated reply suggestions during your broadcast.",
                    })}
                  />
                </TailwindBox>
              }
              control={
                <TailwindDropdown
                  showSearch={false}
                  className={["h-10"]}
                  name='ai_chat_enabled'
                  fullHeight={true}
                  fullWidth={true}
                  borderColor={"border-gray-500"}
                  backgroundColor={"primary"}
                  borderWidth={["border"]}
                  value={aiChatStore!.isUserEnabled}
                  items={[
                    {
                      label: intl.formatMessage({
                        id: "settings.on",
                        defaultMessage: "ON",
                      }),
                      value: true,
                    },
                    {
                      label: intl.formatMessage({
                        id: "settings.off",
                        defaultMessage: "OFF",
                      }),
                      value: false,
                    },
                  ]}
                  onChange={onChangeAiChatEnabled}
                />
              }
            />
            <SettingsOptionRow
              label={
                <TailwindBox width='w-full' minWidth='min-w-0'>
                  <TailwindTranslatedText
                    className='mr-1'
                    textColor='text-main-color'
                    descriptor={{
                      id: "broadcast-system-settings-control.ai-chat-position",
                      defaultMessage: "AI suggestions position",
                    }}
                  />
                  <HelpTooltip
                    tooltip={intl.formatMessage({
                      id: "broadcast-system-settings-control.ai-chat-position-tooltip",
                      defaultMessage:
                        "Choose where AI reply suggestions appear in the chat. On smaller screens, suggestions always appear at the top to avoid blocking messages.",
                    })}
                  />
                </TailwindBox>
              }
              control={
                <TailwindDropdown
                  showSearch={false}
                  className={["h-10"]}
                  name='ai_chat_position'
                  fullHeight={true}
                  fullWidth={true}
                  borderColor={"border-gray-500"}
                  backgroundColor={"primary"}
                  borderWidth={["border"]}
                  disabled={!aiChatStore!.isUserEnabled}
                  value={aiChatStore!.recommendationPosition}
                  items={[
                    {
                      label: intl.formatMessage({
                        id: "broadcast-system-settings-control.ai-chat-position-auto",
                        defaultMessage: "Auto",
                      }),
                      value: "auto",
                    },
                    {
                      label: intl.formatMessage({
                        id: "broadcast-system-settings-control.ai-chat-position-top",
                        defaultMessage: "Top",
                      }),
                      value: "top",
                    },
                    {
                      label: intl.formatMessage({
                        id: "broadcast-system-settings-control.ai-chat-position-bottom",
                        defaultMessage: "Bottom",
                      }),
                      value: "bottom",
                    },
                  ]}
                  onChange={onChangeAiChatPosition}
                />
              }
            />
          </>
        )}
        {allowFaceBlur && (
          <SettingsOptionRow
            label={
              <TailwindBox width='w-full' minWidth='min-w-0'>
                <TailwindTranslatedText
                  className='mr-1'
                  textColor='text-main-color'
                  descriptor={{
                    id: "broadcast-system-settings-control.face-blur",
                    defaultMessage: "Blur background faces",
                  }}
                />
                <HelpTooltip
                  tooltip={intl.formatMessage({
                    id: "broadcast-system-settings-control.face-blur-tooltip",
                    defaultMessage:
                      "When enabled, any faces other than yours (the largest face in frame) will be automatically blurred. This helps protect the privacy of anyone who may appear behind you during your broadcast.",
                  })}
                />
              </TailwindBox>
            }
            control={
              <TailwindDropdown
                showSearch={false}
                className={["h-10"]}
                name='face_blur'
                fullHeight={true}
                fullWidth={true}
                borderColor={"border-gray-500"}
                backgroundColor={"primary"}
                borderWidth={["border"]}
                value={faceBlurEnabled}
                items={[
                  {
                    label: intl.formatMessage({
                      id: "settings.on",
                      defaultMessage: "ON",
                    }),
                    value: true,
                  },
                  {
                    label: intl.formatMessage({
                      id: "settings.off",
                      defaultMessage: "OFF",
                    }),
                    value: false,
                  },
                ]}
                onChange={onChangeFaceBlur}
              />
            }
          />
        )}

        {isLiveTranslationActive && voiceOptions.length > 0 && (
          <SettingsOptionRow
            narrowWidth='md:w-52'
            label={
              <TailwindBox width='w-full' minWidth='min-w-0'>
                <TailwindTranslatedText
                  className='mr-1'
                  textColor='text-main-color'
                  descriptor={{
                    id: "broadcast-system-settings-control.translation-voice",
                    defaultMessage: "Live translation voice",
                  }}
                />
                <HelpTooltip
                  tooltip={intl.formatMessage({
                    id: "broadcast-system-settings-control.translation-voice-tooltip",
                    defaultMessage:
                      "Select the voice that will be used for live translation audio. Click the play button to preview the voice.",
                  })}
                />
              </TailwindBox>
            }
            control={
              <TailwindFlex
                width='w-full'
                alignItems='items-center'
                gap='gap-2'>
                <TailwindBox flexGrow='flex-grow' minWidth='min-w-0'>
                  <TailwindDropdown
                    showSearch={false}
                    className={["h-10"]}
                    name='voice-picker'
                    fullHeight={true}
                    fullWidth={true}
                    borderColor={"border-gray-500"}
                    backgroundColor={"primary"}
                    borderWidth={["border"]}
                    value={selectedVoiceIdx}
                    items={voiceOptions.map(voice => {
                      const label = intl.formatMessage(
                        {
                          id: "broadcast-system-settings-control.voice-number",
                        },
                        { number: voice.displayNumber || 1 }
                      );
                      return {
                        label,
                        value: voice.idx,
                      };
                    })}
                    onChange={onChangeVoiceOption}
                  />
                </TailwindBox>
                <TailwindButton
                  padding={["px-3", "py-2"]}
                  fullWidth={false}
                  flexShrink='flex-shrink-0'
                  onClick={onPlayVoicePreview}
                  backgroundColor={"primary"}>
                  <TailwindIcon
                    name={isPreviewPlaying ? "stop" : "volume_up"}
                    fontSize={"text-xl"}
                  />
                </TailwindButton>
              </TailwindFlex>
            }
          />
        )}
        <SettingsOptionRow
          label={
            <TailwindBox width='w-full' minWidth='min-w-0'>
              <TailwindTranslatedText
                className='mr-1'
                textColor='text-main-color'
                descriptor={{
                  id: "broadcast-system-settings-control.display-tabs",
                  defaultMessage: "Display whispers on separate tabs",
                }}
              />
              <HelpTooltip
                tooltip={intl.formatMessage({
                  id: "broadcast-system-settings-control.help-switch-layout-whisper",
                  defaultMessage:
                    "Here, you can choose between two options: viewing whispers in the main chatroom or organizing each member's whispers on separate tabs.",
                })}
              />
            </TailwindBox>
          }
          control={
            <TailwindDropdown
              showSearch={false}
              className={["h-10"]}
              name='sound_enter_leave'
              fullHeight={true}
              fullWidth={true}
              borderColor={"border-gray-500"}
              backgroundColor={"primary"}
              borderWidth={["border"]}
              value={
                modelProducts?.chat_settings?.system_settings
                  .whispers_in_separate_tabs
                  ? true
                  : false
              }
              items={[
                {
                  label: intl.formatMessage({
                    id: "common.yes",
                    defaultMessage: "Yes",
                  }),
                  value: true,
                },
                {
                  label: intl.formatMessage({
                    id: "common.no",
                    defaultMessage: "No",
                  }),
                  value: false,
                },
              ]}
              onChange={onChangeWhisperTabsLayout}
            />
          }
        />
      </TailwindBox>
    </BroadcastingSettingsModal>
  );
};

export default injectIntl(
  inject(
    "broadcastStore",
    "broadcastStreamStore",
    "pricingStore",
    "validationStore",
    "profileStore",
    "layoutStore",
    "nodeChatStore",
    "languageStore",
    "aiChatStore",
    "authStore"
  )(observer(BroadcastSettingsPopover))
);
